




























import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { BidViewObject, ProductTileViewObject, StockInformationViewObject } from '@/types/apiServerContract';
import { TrackingProductAndElement } from '@/project/tracking/gtm/types';

import simplePriceFetchService, { PriceSimpleDataExtended } from '@/project/product/services/simplePriceFetch.service';
import stockFetchService from '@/project/product/services/stockFetch.service';
import GTMService, { ProductTrackingState, TrackingProduct } from '@/project/tracking/gtm/GTM.service';
import authService from '@/core/auth/auth.service';
import serverContextService from '@/core/serverContext.service';

import ProductTileBid from '@/project/product/product-tile/ProductTileBid.vue';
import ProductTileCart from '@/project/product/product-tile/ProductTileCart.vue';
import bus from '@/core/bus';
import { ProductTrackerProcessBatchKey } from '@/project/tracking/gtm/ProductListTracker.vue';

@Component({
    components: {
        ProductTileBid,
        ProductTileCart
    }
})
export default class ProductDetailsBidTile extends Vue {
    @Prop({ required: true }) product!: ProductTileViewObject;
    @Prop({ required: true, type: Function }) productClick!: (product: TrackingProduct) => void;

    private priceData: PriceSimpleDataExtended | null = null;
    private stockData: readonly StockInformationViewObject[] | null = null;
    private bidsData: readonly ProductTileViewObject[] | null = null;
    private showMenu: boolean = false;
    private isERPServiceAvailable = true;

    private pricesLoaded: boolean = false;
    private stockLoaded: boolean = false;

    private cancellationCallbacks: (() => void)[] = [];

    get bid(): BidViewObject | null | undefined {
        return this.product.bid;
    }

    private get productDetailsUrl() {
        return encodeURI(this.product.url);
    }

    private get hasBids() {
        return this.bidsData && this.bidsData.length > 0;
    }

    get hasAlternatives() {
        return this.product.alternatives.length > 0;
    }

    get hasAccessories() {
        return this.product.accessories.length > 0;
    }

    get isUserLoggedIn(): boolean {
        return authService.isLoggedIn();
    }

    get isExternalProductGuide() {
        return serverContextService.isExternalProductGuide;
    }

    get getTrackingState(): ProductTrackingState {
        if (!this.isUserLoggedIn) {
            return ProductTrackingState.NotLoggedIn;
        }

        if (!this.pricesLoaded || !this.stockLoaded) {
            return ProductTrackingState.NoErpResponseYet;
        }

        return this.isERPServiceAvailable ? ProductTrackingState.Complete : ProductTrackingState.ErpNotAvailable;
    }

    created() {
        this.getPriceData();
        this.getStockData();
    }

    mounted() {
        this.trackImpression(true);
    }

    async toggleMenu() {
        this.showMenu = !this.showMenu;
    }

    @Watch('isUserLoggedIn')
    onLoginStateChange(now: boolean, before: boolean) {
        if (now && !before) {
            this.getPriceData();
            this.getStockData();
        }
    }

    async getPriceData() {
        if (this.isExternalProductGuide) {
            this.pricesLoaded = true;
            return;
        }

        this.pricesLoaded = false;

        try {
            if (!this.isUserLoggedIn) {
                return;
            }

            const request = simplePriceFetchService.requestData(this.product.itemId);

            this.cancellationCallbacks.push(request.cancel);

            const result = await request.promise;

            this.priceData = Object.freeze(result);
            this.isERPServiceAvailable = this.isERPServiceAvailable && result.isServiceAvailable;
        } catch {
        } finally {
            this.pricesLoaded = true;
            this.trackImpression();
        }
    }

    async getStockData() {
        this.stockLoaded = false;

        try {
            const request = stockFetchService.requestData(this.product.itemId);

            this.cancellationCallbacks.push(request.cancel);

            const result = await request.promise;

            this.stockData = Object.freeze(result.stockInformation);
            this.isERPServiceAvailable = this.isERPServiceAvailable && result.isServiceAvailable;
        } catch {
        } finally {
            this.stockLoaded = true;
            this.trackImpression();
        }
    }

    trackImpression(force: boolean = false): void {
        if ((!force && (!this.pricesLoaded || !this.stockLoaded)) || this.isExternalProductGuide) {
            return;
        }

        this.$emit('productImpression', { product: this.constructTrackingProduct(), el: this.$el } as TrackingProductAndElement);
    }

    trackAddToBasketChange(deltaQuantity: number) {
        bus.emit(ProductTrackerProcessBatchKey);
        GTMService.trackProductQuantityChange(this.constructTrackingProduct(), deltaQuantity, `bids-${this.$route.name}-bid`);
    }

    private constructTrackingProduct(): TrackingProduct {
        return {
            ...this.product,
            price: this.priceData?.amount,
            stockInfo: this.stockData,
            trackingState: this.getTrackingState
        };
    }

    rawProductClick() {
        this.productClick(this.constructTrackingProduct());
    }
}
